import { useState, type FormEvent } from 'react';
import { Card, Stack, Group, Text, Button, Anchor } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Textarea, Checkbox, Select } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { ApiError } from '@repletoryreach/sdk-api';
import { useEnrol } from '../../../data/useAffiliate';
import { useT } from '../../../i18n/I18nProvider';
import { answerToHtml } from '../../../lib/answer-html';

/**
 * The application. Short on purpose: a name to be known by, a way to reach
 * them, and how they plan to promote it, which is the one answer an operator
 * reads before approving. Everything else can be edited afterwards.
 */
export function EnrolForm({ email, termsMarkdown, termsVersion }: {
  email?: string;
  termsMarkdown?: string;
  termsVersion?: number;
}) {
  const t = useT();
  const enrol = useEnrol();
  const [displayName, setDisplayName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [contactEmail, setContactEmail] = useState(email ?? '');
  const [contactPhone, setContactPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [how, setHow] = useState('');
  const [accepted, setAccepted] = useState(false);
  // The country comes first (owner's decision of 2 October 2026): the programme is South Africa only,
  // so someone elsewhere is told before filling in the rest.
  const [country, setCountry] = useState<'' | 'ZA' | 'other'>('');
  const elsewhere = country === 'other';
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await enrol.mutateAsync({
        display_name: displayName.trim(),
        contact_email: contactEmail.trim(),
        accept_terms: accepted,
        country_iso: 'ZA',
        business_name: businessName.trim() || undefined,
        contact_phone: contactPhone.trim() || undefined,
        website: website.trim() || undefined,
        how_you_promote: how.trim() || undefined
      });
      notifySuccess(t('refer.enrol.sent'));
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: { code?: string; message?: string } } | null) : null;
      setError(body?.error?.code === 'PROGRAMME_SOUTH_AFRICA_ONLY' ? t('refer.enrol.southAfricaOnly') : (body?.error?.message ?? t('refer.enrol.failed')));
    }
  }

  return (
    <Card withBorder>
      <form onSubmit={submit}>
        <Stack gap="md">
          <Stack gap="xs">
            <Text role="label">{t('refer.enrol.title')}</Text>
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('refer.enrol.description')}</Text>
          </Stack>

          {error && <Alert color="error" role="alert">{error}</Alert>}

          <Select
            label={t('refer.enrol.country')}
            description={t('refer.enrol.countryHint')}
            value={country}
            onChange={(e) => setCountry(e.currentTarget.value as 'ZA' | 'other')}
            placeholder={t('refer.enrol.countryChoose')}
            data={[{ value: 'ZA', label: t('refer.enrol.countryZa') }, { value: 'other', label: t('refer.enrol.countryOther') }]}
            required
          />
          {elsewhere && <Alert color="warning" role="status">{t('refer.enrol.southAfricaOnly')}</Alert>}

          {country === 'ZA' && <>
          <TextInput
            label={t('refer.enrol.displayName')}
            description={t('refer.enrol.displayNameHint')}
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
            required
            maxLength={120}
          />
          <TextInput
            label={t('refer.enrol.businessName')}
            value={businessName}
            onChange={(e) => setBusinessName(e.currentTarget.value)}
            maxLength={200}
          />
          <TextInput
            label={t('refer.enrol.contactEmail')}
            description={t('refer.enrol.contactEmailHint')}
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.currentTarget.value)}
            required
          />
          <TextInput
            label={t('refer.enrol.contactPhone')}
            value={contactPhone}
            onChange={(e) => setContactPhone(e.currentTarget.value)}
            maxLength={32}
          />
          <TextInput
            label={t('refer.enrol.website')}
            value={website}
            onChange={(e) => setWebsite(e.currentTarget.value)}
            maxLength={300}
          />
          <Textarea
            label={t('refer.enrol.how')}
            description={t('refer.enrol.howHint')}
            value={how}
            onChange={(e) => setHow(e.currentTarget.value)}
            minRows={3}
          />

          <Stack gap="xs">
            <Checkbox
              label={t('refer.enrol.acceptTerms')}
              checked={accepted}
              onChange={(checked) => setAccepted(checked)}
            />
            <Anchor onClick={() => setShowTerms((v) => !v)} role="button" tabIndex={0}>
              <Text role="caption">
                {showTerms ? t('refer.enrol.hideTerms') : t('refer.enrol.readTerms')}
                {termsVersion ? ` (${t('refer.enrol.version', { version: String(termsVersion) })})` : ''}
              </Text>
            </Anchor>
            {showTerms && termsMarkdown && (
              // The terms as written, not a summary: a partner is agreeing to
              // how they get paid and when it can be taken back. They are kept
              // as Markdown (edited in the admin console) and shown formatted
              // (9 October 2026): answerToHtml escapes everything first, so only
              // headings, bold, paragraphs and lists come through.
              <Card withBorder>
                <div className="rr-refer-terms" data-testid="partner-terms" dangerouslySetInnerHTML={{ __html: answerToHtml(termsMarkdown) }} />
              </Card>
            )}
          </Stack>

          <Group>
            <Button type="submit" loading={enrol.isPending} disabled={!accepted || !displayName.trim() || !contactEmail.trim()}>
              {t('refer.enrol.submit')}
            </Button>
          </Group>
          </>}
        </Stack>
      </form>
    </Card>
  );
}
